import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import { KeyBoardEvents } from '@aurora/shared-types/community/enums';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useRef, useState } from 'react';
import { Overlay, Popover, useClassNameMapper } from 'react-bootstrap';
import type { BadgeViewVariant, ItemType } from '../../../../types/enums';
import type { BadgeViewFragment } from '../../../../types/graphql-types';
import type { ItemViewVariantFC } from '../../../entities/types';
import useTranslation from '../../../useTranslation';
import localStyles from './BadgeViewStandard.module.pcss';
import BadgeIcon from '@aurora/shared-client/components/badges/BadgeIcon/BadgeIcon';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';

/**
 * Badge view standard component to render the regular badge in the badge set card.
 *
 * @constructor
 * @author Gaston Manzano
 */
const BadgeViewStandard: ItemViewVariantFC<
  BadgeViewFragment,
  ItemType.BADGE,
  BadgeViewVariant.STANDARD
> = ({ entity: badge, isEarned }) => {
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const target = useRef<HTMLDivElement>(null);
  const i18n = useTranslation(EndUserComponent.BADGE_VIEW_STANDARD);
  const { FormattedMessage } = i18n;
  const cx = useClassNameMapper(localStyles);
  const { description, title, icon } = badge;

  /**
   * Renders the tooltip of the badge.
   */
  function renderBadgeTooltip(): React.ReactElement {
    return (
      <Overlay
        show={showTooltip}
        target={target.current}
        placement={'top'}
        rootClose
        onHide={() => setShowTooltip(false)}
        popperConfig={dropdownPopperConfig}
        transition={false}
      >
        <Popover id={'Badge-Tooltip-Popover'}>
          <Popover.Content data-testid={'Badge-Tooltip-Content'}>
            <FormattedMessage
              id={'badge.tooltip.content'}
              values={{
                bold: (chunks: string) => <strong>{chunks}</strong>,
                title,
                br: <br />,
                description
              }}
            />
          </Popover.Content>
        </Popover>
      </Overlay>
    );
  }

  function renderBadgeTooltipButton(): React.ReactNode {
    return (
      <div
        role="button"
        data-testid={'Badge-Tooltip'}
        onClick={event => event.stopPropagation()}
        onKeyDown={event => {
          if (event.key === KeyBoardEvents.ENTER || event.key === KeyBoardEvents.SPACE) {
            event.stopPropagation();
          }
        }}
        tabIndex={-1}
      >
        {renderBadgeTooltip()}
      </div>
    );
  }

  function renderBadgeIconButton(): React.ReactNode {
    return (
      <div
        role={'button'}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onKeyDown={event => event.stopPropagation()}
        ref={target}
        data-testid={`Badge.${isEarned ? 'Earned' : 'Unearned'}`}
        tabIndex={-1}
      >
        <BadgeIcon
          href={icon?.url}
          className={cx(`lia-badge-icon lia-badge-${isEarned ? 'earned' : 'unearned'}`)}
          alt={title}
          size={IconSize.PX_60}
        />
      </div>
    );
  }

  return (
    <div className={cx('lia-badge-container')}>
      {renderBadgeIconButton()}
      {renderBadgeTooltipButton()}
    </div>
  );
};

export default BadgeViewStandard;
